/**
 * Short-lived signed handoff between Capacitor Browser (SFSafariViewController) and WKWebView.
 * OAuth session cookies exist only in the in-app browser; the WebView needs a one-time bridge.
 */

import { SignJWT, jwtVerify } from 'jose'

export const CAPACITOR_HANDOFF_PARAM = 'handoff'
export const CAPACITOR_HANDOFF_IN_PROGRESS_KEY = 'wagamour_capacitor_handoff_in_progress'

const HANDOFF_PURPOSE = 'capacitor_oauth_handoff'
const HANDOFF_TTL_SECONDS = 90

function handoffSecret(): Uint8Array | null {
  const secret = process.env.NEXTAUTH_SECRET?.trim()
  if (!secret) return null
  return new TextEncoder().encode(secret)
}

export async function createCapacitorHandoffToken(userId: string): Promise<string | null> {
  const secret = handoffSecret()
  if (!secret || !userId) return null
  return new SignJWT({ purpose: HANDOFF_PURPOSE })
    .setProtectedHeader({ alg: 'HS256' })
    .setSubject(userId)
    .setIssuedAt()
    .setExpirationTime(`${HANDOFF_TTL_SECONDS}s`)
    .sign(secret)
}

export async function verifyCapacitorHandoffToken(
  token: string | null | undefined
): Promise<{ userId: string } | null> {
  if (!token?.trim()) return null
  const secret = handoffSecret()
  if (!secret) return null
  try {
    const { payload } = await jwtVerify(token, secret, {
      algorithms: ['HS256'],
      maxTokenAge: `${HANDOFF_TTL_SECONDS}s`,
    })
    if (payload.purpose !== HANDOFF_PURPOSE) return null
    const userId = payload.sub
    if (!userId || typeof userId !== 'string') return null
    return { userId }
  } catch {
    return null
  }
}

export function markCapacitorHandoffInProgress(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(CAPACITOR_HANDOFF_IN_PROGRESS_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

export function clearCapacitorHandoffInProgress(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CAPACITOR_HANDOFF_IN_PROGRESS_KEY)
  } catch {
    // ignore
  }
}

export function isCapacitorHandoffInProgress(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = sessionStorage.getItem(CAPACITOR_HANDOFF_IN_PROGRESS_KEY)
    if (!raw) return false
    const started = Number(raw)
    if (!Number.isFinite(started)) return false
    return Date.now() - started < HANDOFF_TTL_SECONDS * 1000
  } catch {
    return false
  }
}

export function buildCapacitorHandoffConsumeUrl(handoff: string, nextPath: string): string {
  const params = new URLSearchParams({
    token: handoff,
    next: nextPath,
  })
  return `/api/auth/capacitor-handoff/consume?${params.toString()}`
}

/** Temporary diagnostics for native OAuth return (remove when stable). */
export function logCapacitorOAuth(step: string, detail?: Record<string, unknown>): void {
  const payload = detail ? { ...detail } : undefined
  if (typeof window !== 'undefined') {
    console.log('[CapacitorOAuth]', step, payload ?? '')
    return
  }
  console.log('[CapacitorOAuth]', step, payload ?? '')
}
